import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  congestionColors,
  ParkingPlace,
  reverseCongestionMapper,
} from 'src/interfaces/user-mobile/parking.interface';
import { instance } from 'src/apis';

import dropDowmButton from 'src/assets/images/user-mobile/parking/dropdown-button.svg';

import ControlOnebyOne from './ControlOnebyOne';
import DisabledBox from './DisabledBox';

interface SettingBoxProps {
  place: ParkingPlace;
  isAllOn: boolean;
  closeAll: boolean;
  refetch: () => Promise<void>;
}

const levels = ['여유', '보통', '혼잡', '만차'] as const;

export default function SettingBox({
  place,
  isAllOn,
  closeAll,
  refetch,
}: SettingBoxProps) {
  const [isOpen, setIsOpen] = useState(true);

  const handleSwitchChange = async () => {
    try {
      const newEnabled = !place.parkingPlaceEnabled;
      await instance.patch('/parking/place/state', null, {
        params: {
          parkingPlaceId: place.id,
          parkingPlaceEnabled: newEnabled,
        },
      });

      if (!newEnabled) {
        await instance.patch('/parking/place/congestion', null, {
          params: {
            parkingPlaceId: place.id,
            congestionLevel: 'LOW',
          },
        });
      }
      await refetch();
    } catch (e) {
      console.error('스위치 변경 실패', e);
    }
  };

  const handleLevelChange = async (level: (typeof levels)[number]) => {
    try {
      await instance.patch('/parking/place/congestion', null, {
        params: {
          parkingPlaceId: place.id,
          congestionLevel: reverseCongestionMapper[level],
        },
      });
      await refetch();
    } catch (e) {
      console.error('혼잡도 변경 실패', e);
    }
  };

  useEffect(() => {
    if (closeAll) {
      setIsOpen(false);
    }
  }, [closeAll]);

  return (
    <Box>
      <Title onClick={() => setIsOpen(prev => !prev)}>
        <TitleLeft>
          <span>{place.name}</span>
          <Badge
            level={
              isAllOn && place.parkingPlaceEnabled
                ? place.congestionLevel
                : '운영마감'
            }
          >
            {isAllOn && place.parkingPlaceEnabled
              ? place.congestionLevel
              : '운영마감'}
          </Badge>
        </TitleLeft>
        <ToggleButton isOpen={isOpen}>
          <img src={dropDowmButton} alt="toggle" width={16} height={16} />
        </ToggleButton>
      </Title>
      <ContentWrapper isOpen={isOpen}>
        <Content>
          <ControlOnebyOne
            isOn={isAllOn && place.parkingPlaceEnabled}
            setIsOn={handleSwitchChange}
            disabled={!isAllOn}
          />
          {isAllOn && place.parkingPlaceEnabled ? (
            <LevelBox>
              {levels.map(level => (
                <LevelButton
                  key={level}
                  level={level}
                  isActive={place.congestionLevel === level}
                  onClick={() => handleLevelChange(level)}
                >
                  {level}
                </LevelButton>
              ))}
            </LevelBox>
          ) : (
            <DisabledBox />
          )}
        </Content>
      </ContentWrapper>
    </Box>
  );
}

const Box = styled.div`
  width: 100%;
  padding: 10px 20px 0 20px;
  background-color: #ffffff;
  padding-top: 16px;
  border-bottom: 1px solid #f1f1f1;
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
`;

const TitleLeft = styled.div`
  color: var(--Font-02_black, #111);
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 140% */
  letter-spacing: -0.5px;
`;

const Badge = styled.span<{ level: keyof typeof congestionColors }>`
  margin-left: 6px;
  padding: 2px 7px;
  border-radius: 15px;
  border: 1px solid ${({ level }) => congestionColors[level].textColor};
  color: ${({ level }) => congestionColors[level].textColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`;

const ContentWrapper = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  transition: all 0.3s ease;
  max-height: ${({ isOpen }) => (isOpen ? '200px' : '0')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: ${({ isOpen }) =>
    isOpen ? 'translateY(0)' : 'translateY(-10px)'};
`;

const Content = styled.div`
  padding-bottom: 15px;
  justify-content: space-between;
  display: flex;
  align-items: center;
  width: 100%;
`;

const LevelBox = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 8px;
  height: 48px;
  border-radius: 16px;
  width: 100%;
  border: 1px solid #f1f1f1;
  background: #f2f2f5;
  padding: 4px;
`;

const LevelButton = styled.button<{ isActive: boolean; level: string }>`
  padding: 9px 16px;
  width: 100%;
  margin: 0 2px 0 2px;

  border-radius: 12px;
  background: ${({ isActive, level }) =>
    isActive
      ? congestionColors[level as keyof typeof congestionColors].bgColor
      : 'transparent'};
  color: ${({ isActive }) => (isActive ? '#fff' : '#111')};

  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px; /* 146.667% */
  letter-spacing: -0.375px;

  border: none;
  cursor: pointer;
  transition: 0.2s ease all;
`;
